// app-ui/src/components/canvas/FlowNode.tsx
import React, { memo, useCallback } from 'react';
import { Handle, Position, NodeProps, Node, Edge } from 'reactflow';
import { NodeAction } from './NodeActionsPopup';
import { getLatestBuild } from '@/shared/lib/buildStatusTracker';

export type FlowNodeType = 'userFlow' | 'highLevelRequirement' | 'lowLevelRequirement' | 'testCase';

/** Child requirement type for a given parent node level. */
export function childNodeTypeForParent(parentType: FlowNodeType): FlowNodeType | null {
    switch (parentType) {
        case 'userFlow':
            return 'highLevelRequirement';
        case 'highLevelRequirement':
            return 'lowLevelRequirement';
        case 'lowLevelRequirement':
            return 'testCase';
        default:
            return null;
    }
}

export function requirementTypeLabel(nodeType: FlowNodeType): string {
    switch (nodeType) {
        case 'userFlow':
            return 'User Flow';
        case 'highLevelRequirement':
            return 'High Level Requirement';
        case 'lowLevelRequirement':
            return 'Low Level Requirement';
        case 'testCase':
            return 'Test Case';
    }
}

export interface FlowNodeData {
    name: string;
    description?: string;
    sno: number;
    uiid: string;
    id: string;
    originalData?: any;
    nodeType?: FlowNodeType;
}

interface FlowNodeProps extends NodeProps<FlowNodeData & { projectId?: string | number }> {
    onNodeDoubleClick?: (nodeId: string, data: FlowNodeData) => void;
    onNodeContextMenu?: (event: React.MouseEvent, nodeId: string) => void;
    nodeType?: FlowNodeType;
}

export interface NodeActionsPopupState {
    isOpen: boolean;
    position: { x: number; y: number };
    node: Node | null;
}

export const nodeActionsPopupInitialState: NodeActionsPopupState = {
    isOpen: false,
    position: { x: 0, y: 0 },
    node: null
};

export interface FlowNodeHandlersConfig {
    nodes: Node[];
    edges: Edge[];
    setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
    setSelectedNodeId: React.Dispatch<React.SetStateAction<string | null>>;
    setHighlightedNodeIds: React.Dispatch<React.SetStateAction<Set<string>>>;
    setHighlightedEdgeIds: React.Dispatch<React.SetStateAction<Set<string>>>;
    setSelectedFlowNode: React.Dispatch<React.SetStateAction<FlowNodeData | null>>;
    setIsFlowModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    selectedFlowNode: FlowNodeData | null;
    setNodeActionsPopup: React.Dispatch<React.SetStateAction<NodeActionsPopupState>>;
}

export const useFlowNodeHandlers = ({
    nodes,
    edges,
    setNodes,
    setSelectedNodeId,
    setHighlightedNodeIds,
    setHighlightedEdgeIds,
    setSelectedFlowNode,
    setIsFlowModalOpen,
    selectedFlowNode,
    setNodeActionsPopup
}: FlowNodeHandlersConfig) => {
    const getNodeLevel = useCallback((node: Node): number => {
        if (node.type === 'userFlowNode') return 0;
        if (node.type === 'highLevelRequirementNode') return 1;
        if (node.type === 'lowLevelRequirementNode') return 2;
        if (node.type === 'testCaseNode') return 3;

        if (node.id.startsWith('userflow_')) return 0;
        if (node.id.startsWith('highlevelrequirement_')) return 1;
        if (node.id.startsWith('lowlevelrequirement_')) return 2;
        if (node.id.startsWith('testcase_')) return 3;

        return -1;
    }, []);

    const handleNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
        event.stopPropagation();

        const clickedNodeLevel = getNodeLevel(node);
        const connectedNodeIds = new Set<string>();
        const connectedEdgeIds = new Set<string>();
        connectedNodeIds.add(node.id);

        const findAncestors = (nodeId: string, visited: Set<string>) => {
            if (visited.has(nodeId)) return;
            visited.add(nodeId);

            const currentNode = nodes.find(n => n.id === nodeId);
            if (!currentNode) return;
            const currentNodeLevel = getNodeLevel(currentNode);

            edges.forEach(edge => {
                if (edge.target === nodeId && !visited.has(edge.source)) {
                    const sourceNode = nodes.find(n => n.id === edge.source);
                    if (sourceNode) {
                        const sourceLevel = getNodeLevel(sourceNode);
                        if (sourceLevel === currentNodeLevel - 1) {
                            connectedNodeIds.add(edge.source);
                            connectedEdgeIds.add(edge.id);
                            findAncestors(edge.source, visited);
                        }
                    }
                }
            });
        };

        const findChildren = (nodeId: string, visited: Set<string>) => {
            if (visited.has(nodeId)) return;
            visited.add(nodeId);

            const currentNode = nodes.find(n => n.id === nodeId);
            if (!currentNode) return;
            const currentNodeLevel = getNodeLevel(currentNode);

            edges.forEach(edge => {
                if (edge.source === nodeId && !visited.has(edge.target)) {
                    const targetNode = nodes.find(n => n.id === edge.target);
                    if (targetNode) {
                        const targetLevel = getNodeLevel(targetNode);
                        if (targetLevel !== currentNodeLevel && targetLevel > currentNodeLevel) {
                            connectedNodeIds.add(edge.target);
                            connectedEdgeIds.add(edge.id);
                            findChildren(edge.target, visited);
                        }
                    }
                }
            });
        };

        findAncestors(node.id, new Set());
        findChildren(node.id, new Set());

        setSelectedNodeId(node.id);
        setHighlightedNodeIds(connectedNodeIds);
        setHighlightedEdgeIds(connectedEdgeIds);
    }, [edges, nodes, getNodeLevel, setHighlightedEdgeIds, setHighlightedNodeIds, setSelectedNodeId]);

    const handleNodeDoubleClick = useCallback((event: React.MouseEvent, node: Node) => {
        event.stopPropagation();

        const isFlowNode = node.type && ['userFlowNode', 'highLevelRequirementNode', 'lowLevelRequirementNode', 'testCaseNode'].includes(node.type);

        if (isFlowNode) {
            const flowData = node.data as FlowNodeData;
            setSelectedFlowNode(flowData);
            setIsFlowModalOpen(true);
            return;
        }

        const targetElement = event.target as HTMLElement;
        let nodeElement: HTMLElement | null = null;
        let currentElement: HTMLElement | null = targetElement;

        while (currentElement && !nodeElement) {
            if (currentElement.classList.contains('react-flow__node') ||
                currentElement.getAttribute('data-id') === node.id ||
                currentElement.getAttribute('data-nodeid') === node.id ||
                currentElement.id === node.id) {
                nodeElement = currentElement;
                break;
            }
            currentElement = currentElement.parentElement;
        }

        if (!nodeElement && node.id) {
            const selectors = [
                `[data-id="${node.id}"]`,
                `[data-nodeid="${node.id}"]`,
                `#${node.id}`,
                `.react-flow__node[data-id="${node.id}"]`
            ];

            for (const selector of selectors) {
                const foundElement = document.querySelector(selector) as HTMLElement;
                if (foundElement) {
                    nodeElement = foundElement;
                    break;
                }
            }
        }

        let popupX = event.clientX;
        let popupY = event.clientY;

        if (nodeElement) {
            const rect = nodeElement.getBoundingClientRect();
            popupX = rect.right + 10;
            popupY = rect.top + (rect.height / 2);
        }

        setNodeActionsPopup({
            isOpen: true,
            position: { x: popupX, y: popupY },
            node
        });
    }, [setIsFlowModalOpen, setNodeActionsPopup, setSelectedFlowNode]);

    const handleFlowNodeDoubleClick = useCallback((nodeId: string, nodeData: FlowNodeData) => {
        setSelectedFlowNode(nodeData);
        setIsFlowModalOpen(true);
    }, [setIsFlowModalOpen, setSelectedFlowNode]);

    const handleFlowSave = useCallback((nodeId: string, updatedData: Partial<FlowNodeData>) => {
        setNodes((nds) =>
            nds.map((node) => {
                if (node.id === nodeId && (
                    node.type === 'userFlowNode' ||
                    node.type === 'highLevelRequirementNode' ||
                    node.type === 'lowLevelRequirementNode' ||
                    node.type === 'testCaseNode'
                )) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            ...updatedData
                        }
                    };
                }
                return node;
            })
        );

        if (selectedFlowNode && selectedFlowNode.id === nodeId) {
            setSelectedFlowNode({
                ...selectedFlowNode,
                ...updatedData
            });
        }
    }, [selectedFlowNode, setNodes, setSelectedFlowNode]);

    const handlePaneClickWithHighlight = useCallback(() => {
        setNodeActionsPopup(nodeActionsPopupInitialState);
        setSelectedNodeId(null);
        setHighlightedNodeIds(new Set());
        setHighlightedEdgeIds(new Set());
    }, [setHighlightedEdgeIds, setHighlightedNodeIds, setNodeActionsPopup, setSelectedNodeId]);

    return {
        getNodeLevel,
        handleNodeClick,
        handleNodeDoubleClick,
        handleFlowNodeDoubleClick,
        handleFlowSave,
        handlePaneClickWithHighlight
    };
};

export const getNodeType = (node: Node) => {
    const nodeId = node.id;
    const nodeData = node.data as any;

    if (nodeId.startsWith('testcase_') || nodeData?.isTestCase === true || node.type === 'testCaseNode') {
        return 'testcase';
    }
    if (nodeId.startsWith('lowlevelrequirement_') || nodeData?.isLlr === true || node.type === 'lowLevelRequirementNode') {
        return 'llr';
    }
    return 'other';
};

export interface BuildNodeActionsOptions {
    onBuildFeature?: (context: { nodeId: string; rowIndex?: number }) => void;
    onBuildEpic?: (context: { nodeId: string; rowIndex?: number }) => void;
    onGenerateChildRequirements?: (nodeId: string, rowIndex: number) => void;
}

export const buildNodeActions = (node: Node, options: BuildNodeActionsOptions = {}): NodeAction[] => {
    const nodeId = node.id;
    const nodeType = getNodeType(node);
    const nodeData = node.data as any;
    const rowIndex = nodeData?.rowIndex ?? 0;
    const actions: NodeAction[] = [];

    if (nodeType === 'llr' && node.type === 'filteredRequirementNode') {
        if (options.onBuildFeature) {
            actions.push({
                id: 'build-feature',
                label: 'Build the Feature',
                className: 'font-bold text-green-600',
                onClick: () => options.onBuildFeature?.({ nodeId, rowIndex })
            });
        }
    }

    if (nodeType === 'hlr' && node.type === 'filteredRequirementNode') {
        if (options.onBuildEpic) {
            actions.push({
                id: 'build-epic',
                label: 'Build Epic',
                className: 'font-bold text-green-600',
                onClick: () => options.onBuildEpic?.({ nodeId, rowIndex })
            });
        }
    }

    if (nodeType !== 'testcase' && node.type === 'filteredRequirementNode' && options.onGenerateChildRequirements) {
        actions.push({
            id: 'generate-child-req',
            label: 'Generate Child Requirements',
            onClick: () => {
                if (nodeId && !nodeId.startsWith('testcase_')) {
                    options.onGenerateChildRequirements?.(nodeId, rowIndex);
                }
            }
        });
    }

    return actions;
};

// Color configuration for different node types
const nodeTypeColors: Record<FlowNodeType, {
    gradient: string;
    border: string;
    borderSelected: string;
    ring: string;
    badge: string;
    handle: string;
}> = {
    userFlow: {
        gradient: 'from-green-50 to-emerald-50',
        border: 'border-green-300',
        borderSelected: 'border-green-500',
        ring: 'ring-green-200',
        badge: 'from-green-500 to-emerald-600',
        handle: '#10b981'
    },
    highLevelRequirement: {
        gradient: 'from-blue-50 to-cyan-50',
        border: 'border-blue-300',
        borderSelected: 'border-blue-500',
        ring: 'ring-blue-200',
        badge: 'from-blue-500 to-cyan-600',
        handle: '#3b82f6'
    },
    lowLevelRequirement: {
        gradient: 'from-purple-50 to-violet-50',
        border: 'border-purple-300',
        borderSelected: 'border-purple-500',
        ring: 'ring-purple-200',
        badge: 'from-purple-500 to-violet-600',
        handle: '#8b5cf6'
    },
    testCase: {
        gradient: 'from-orange-50 to-amber-50',
        border: 'border-orange-300',
        borderSelected: 'border-orange-500',
        ring: 'ring-orange-200',
        badge: 'from-orange-500 to-amber-600',
        handle: '#f97316'
    }
};

const FlowNode: React.FC<FlowNodeProps> = ({ data, id, selected, nodeType: propNodeType }) => {
    const { name, sno, nodeType: dataNodeType, uiid, buildStatus: dataBuildStatus, originalData, projectId } = data;
    
    // Determine node type: prop > data > default to userFlow
    const currentNodeType: FlowNodeType = propNodeType || dataNodeType || 'userFlow';
    const colors = nodeTypeColors[currentNodeType];
    
    // Check build status for low level requirements
    const isLowLevelRequirement = currentNodeType === 'lowLevelRequirement' || id.startsWith('lowlevelrequirement_');
    let buildStatus: string | null = dataBuildStatus || null;
    
    if (isLowLevelRequirement) {
        // Try to get build status from localStorage tracking or originalData
        const rowUiid = uiid || data.id;
        if (rowUiid && projectId) {
            const trackedBuild = getLatestBuild(projectId, rowUiid);
            buildStatus = trackedBuild?.build_status || originalData?.build_status || 'Not Started';
        } else if (!buildStatus) {
            buildStatus = originalData?.build_status || 'Not Started';
        }
    }

    return (
        <div
            className={`
                min-w-[200px] max-w-[300px]
                bg-gradient-to-br ${colors.gradient}
                border-2 rounded-2xl p-4 shadow-md
                hover:shadow-lg transition-all duration-200 cursor-pointer
                ${selected ? `${colors.borderSelected} ring-2 ${colors.ring}` : colors.border}
                relative flex items-center justify-center
            `}
        >
            {/* Order Badge */}
            <div className={`absolute -top-3 -left-3 w-8 h-8 bg-gradient-to-br ${colors.badge} text-white rounded-full flex items-center justify-center text-xs font-bold shadow-md z-10`}>
                {sno}
            </div>

            {/* Build Status Indicator - Only for Low Level Requirements */}
            {isLowLevelRequirement && (() => {
                const status = buildStatus || 'Not Started';
                const getStatusColor = () => {
                    if (status === 'Completed') return 'bg-green-500 opacity-100';
                    if (status === 'In Progress') return 'bg-blue-500 opacity-100';
                    if (status === 'Error') return 'bg-red-500 opacity-100';
                    return 'bg-gray-500 opacity-100';
                };
                
                const getStatusLabel = () => {
                    if (status === 'Completed') return 'GC';
                    if (status === 'In Progress') return 'IN';
                    if (status === 'Error') return 'ER';
                    return 'NS';
                };
                
                return (
                    <div 
                        className={`absolute -top-3 -right-3 w-8 h-8 ${getStatusColor()} rounded-full shadow-lg z-10 flex items-center justify-center text-white text-xs font-bold`} 
                        title={status}
                    >
                        {getStatusLabel()}
                    </div>
                );
            })()}

            {/* Flow Name Only - Centered */}
            <div className="font-semibold text-gray-800 text-base text-center line-clamp-2">
                {name || `Flow ${sno}`}
            </div>

            {/* Connection Handles */}
            {/* Parent connection (top center) */}
            <Handle
                type="target"
                position={Position.Top}
                id="parent"
                style={{
                    top: -6,
                    left: '50%',
                    width: 12,
                    height: 12,
                    background: colors.handle,
                    border: `2px solid ${colors.handle}`,
                    borderRadius: '50%',
                    opacity: 0.8,
                    zIndex: 9999,
                    position: 'absolute',
                    transform: 'translate(-50%, -50%)',
                }}
            />

            {/* Child connection (bottom center) */}
            <Handle
                type="source"
                position={Position.Bottom}
                id="child"
                style={{
                    bottom: -6,
                    left: '50%',
                    width: 12,
                    height: 12,
                    background: colors.handle,
                    border: `2px solid ${colors.handle}`,
                    borderRadius: '50%',
                    opacity: 0.8,
                    zIndex: 9999,
                    position: 'absolute',
                    transform: 'translate(-50%, 50%)',
                }}
            />

            {/* Sibling connections (left/right) */}
            <Handle
                type="target"
                position={Position.Left}
                id="sibling-in"
                style={{
                    left: -6,
                    top: '50%',
                    width: 12,
                    height: 12,
                    background: colors.handle,
                    border: `2px solid ${colors.handle}`,
                    borderRadius: '50%',
                    opacity: 0.8,
                    zIndex: 9999,
                    position: 'absolute',
                    transform: 'translateY(-50%)',
                }}
            />

            <Handle
                type="source"
                position={Position.Right}
                id="sibling-out"
                style={{
                    right: -6,
                    top: '50%',
                    width: 12,
                    height: 12,
                    background: colors.handle,
                    border: `2px solid ${colors.handle}`,
                    borderRadius: '50%',
                    opacity: 0.8,
                    zIndex: 9999,
                    position: 'absolute',
                    transform: 'translateY(-50%)',
                }}
            />
        </div>
    );
};

export default memo(FlowNode);

